import { useCallback, useMemo, useState } from "react";
import { Box, Skeleton, Typography, useTheme } from "@mui/material";
import type { ExternalPrice, SearchGeneric, SearchProduct } from "@interfaces";
import { ItemType } from "@interfaces";
import { Modal } from "@components";
import {
  numberFormat,
  pharmacies,
  trimmedLaboratory,
  trimString,
  onContactUsClick,
  cleanAndFormatProductCardText,
  productSubCategoriesDetails,
} from "@utils";
import { format } from "date-fns";
import { getOldestExternalPriceUpdateDate } from "./getOldestExternalPriceUpdateDate";
import { min } from "lodash";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import {
  ArrowForwardIcon,
  ComparativeTableContainer,
  ComparativeTableDataRow,
  ComparativeTableRow,
  ContactUsText,
  DataContainer,
  ExpensivePrice,
  HeaderRow,
  LastUpdateMessage,
  MedicineContainer,
  MedicineDescription,
  MedicineImage,
  MedicineInfoColumn,
  MedicineName,
  MedicineNoPriceInfo,
  MedicinePriceAmount,
  MedicinePriceColumn,
  MedicinePriceContainer,
  MedicinePriceText,
  MedicineWrapper,
  ModalContent,
  ModalContentContainer,
  NameInfo,
  PharmacyInfo,
  Price,
  PriceNotFound,
  PriceNotFoundContainer,
  PriceRow,
  RowNumber,
  SpacedRow,
  StyledIconButton,
  StyledPremiumIcon,
  Tag,
  TagContainer,
  TagSpan,
  TagText,
} from "./SearchedMedicineCard.styles";

interface MedicineCardProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  medicine: SearchProduct | SearchGeneric;
  isTreatmentCard?: boolean;
}

export function SearchedMedicineCard({
  medicine,
  isTreatmentCard = false,
  ...props
}: MedicineCardProps) {
  const isGeneric = (medicine.type ?? "") === ItemType.GENERIC;

  if (isGeneric) {
    return (
      <MedicineWrapper isButton={!isTreatmentCard} {...props}>
        <GenericMedicineCard
          genericMedicine={medicine as SearchGeneric}
          isTreatmentCard={isTreatmentCard}
        />
      </MedicineWrapper>
    );
  } else {
    return (
      <MedicineWrapper isButton={!isTreatmentCard} {...props}>
        <ProductCard
          product={medicine as SearchProduct}
          isTreatmentCard={isTreatmentCard}
        />
      </MedicineWrapper>
    );
  }
}

interface SearchedMedicineCardSkeletonProps {
  showImageSkeleton?: boolean;
}

SearchedMedicineCard.Skeleton = function SearchedMedicineCardSkeleton({
  showImageSkeleton = true,
}: SearchedMedicineCardSkeletonProps) {
  return (
    <MedicineContainer style={{ borderBottom: "1px solid #eaeaea", maxHeight: "128px" }}>
      <SpacedRow>
        {showImageSkeleton && (
          <Skeleton
            variant="rounded"
            width={100}
            height={100}
            style={{ marginLeft: "16px" }}
          />
        )}
        <DataContainer isSkeleton={true}>
          <Skeleton width="40%" height="24px" />
          <Skeleton width="60%" height="20px" />
          <Skeleton width="30%" height="20px" />
          <Skeleton
            variant="text"
            width="30%"
            height="20px"
            sx={{ alignSelf: "flex-end" }}
          />
        </DataContainer>
      </SpacedRow>
    </MedicineContainer>
  );
};

interface ProductCardProps {
  product: SearchProduct;
  isTreatmentCard: boolean;
}

function ProductCard({ product, isTreatmentCard }: ProductCardProps) {
  const theme = useTheme();

  const showProductActivePrinciple =
    product.name && product.activePrinciple && product.name !== product.activePrinciple;

  const showProductCompleteFormat = isTreatmentCard && product.completeFormat;

  const productMinPrice = min([product.price, product.discount?.price]);
  const showSincePrice = !isTreatmentCard;

  const productSubCategories = productSubCategoriesDetails(product);
  const showCheckPrescriptionBanner = product.prescriptionType === "CHECK";

  const showLowStockBanner =
    (product.availability?.status ?? "") === "SOLD_OUT" &&
    productSubCategories.isMedicine &&
    !productSubCategories.isInAnotherAllowedSubCategories &&
    !showCheckPrescriptionBanner;

  const [imageSrc, setImageSrc] = useState(
    () =>
      product.imagesUrl?.find(Boolean) ?? "/assets/images/placeholder_medicine_image.png",
  );

  const [showFullImage, setShowFullImage] = useState(false);

  const handleImageClick = useCallback(() => {
    setShowFullImage((prev) => !prev);
  }, []);

  const [modalOpen, setModalOpen] = useState(false);
  const changeModalOpenState = () => setModalOpen((prev) => !prev);

  return (
    <MedicineContainer>
      <SpacedRow>
        <MedicineImage
          src={imageSrc}
          alt={product.name}
          onClick={handleImageClick}
          onError={() => {
            setImageSrc("/assets/images/placeholder_medicine_image.png");
          }}
        />
        <DataContainer isTreatmentCard={isTreatmentCard}>
          <SpacedRow>
            <MedicineName>{trimString(product.name)}</MedicineName>
          </SpacedRow>

          {(showProductActivePrinciple || showProductCompleteFormat) && (
            <MedicineDescription>
              {cleanAndFormatProductCardText(
                `${
                  showProductActivePrinciple ? trimString(product.activePrinciple) : ""
                }${
                  showProductCompleteFormat
                    ? ` · ${trimString(product.completeFormat, 50)}`
                    : ""
                }`,
              )}
            </MedicineDescription>
          )}

          <MedicineDescription>
            {cleanAndFormatProductCardText(
              `${trimString(product.concentration)} · ${trimmedLaboratory(
                product.laboratory,
              )}`,
            )}
          </MedicineDescription>

          {product.quantity && product.unit && (
            <MedicineDescription color={theme.palette.text.primary}>
              {`${product.quantity} ${product.unit}`}
            </MedicineDescription>
          )}

          {showSincePrice && (
            <PriceRow>
              <MedicinePriceContainer>
                {productMinPrice ? (
                  <>
                    <MedicinePriceText>Desde</MedicinePriceText>
                    <MedicinePriceAmount>
                      ${numberFormat(productMinPrice)}
                    </MedicinePriceAmount>
                  </>
                ) : (
                  <MedicineNoPriceInfo>No encontrado</MedicineNoPriceInfo>
                )}
              </MedicinePriceContainer>
            </PriceRow>
          )}
        </DataContainer>

        {!isTreatmentCard && <ArrowForwardIcon />}
      </SpacedRow>

      {showCheckPrescriptionBanner && (
        <TagContainer>
          <Tag backgroundColor={"#D3EAFD"} textColor={theme.palette.info.main}>
            <TagText>
              Medicamento con{" "}
              <TagSpan textColor={theme.palette.info.main}>receta cheque</TagSpan>
            </TagText>
          </Tag>
        </TagContainer>
      )}
      {showLowStockBanner && (
        <TagContainer>
          <Tag backgroundColor={"#FBEAEA"} textColor={theme.palette.error.main}>
            <TagText>
              <TagSpan textColor={theme.palette.error.main}>Baja disponibilidad</TagSpan>{" "}
              en el mercado
            </TagText>
          </Tag>
        </TagContainer>
      )}
      {isTreatmentCard && (
        <ComparativeTable
          modalOpen={modalOpen}
          changeModalOpenState={changeModalOpenState}
          medicine={product}
        />
      )}
      <LastUpdateModal
        medicine={product}
        modalOpen={modalOpen}
        changeModalOpenState={changeModalOpenState}
      />
      {isTreatmentCard && showFullImage && (
        <Modal isOpen={showFullImage} onClose={handleImageClick}>
          <img
            src={imageSrc}
            alt={product.name}
            style={{ width: "100%", height: "auto", objectFit: "contain" }}
            onError={() => {
              setImageSrc("/assets/images/placeholder_medicine_image.png");
            }}
          />
        </Modal>
      )}
    </MedicineContainer>
  );
}

interface GenericMedicineCardProps {
  genericMedicine: SearchGeneric;
  isTreatmentCard: boolean;
}

function GenericMedicineCard({
  genericMedicine,
  isTreatmentCard,
}: GenericMedicineCardProps) {
  const { activePrinciple, completeFormat, concentration, price } = genericMedicine;

  const showSincePrice = !isTreatmentCard && price;
  const [modalOpen, setModalOpen] = useState(false);
  const changeModalOpenState = () => setModalOpen((prev) => !prev);

  return (
    <MedicineContainer>
      <SpacedRow>
        <MedicineImage
          src={"/assets/images/placeholder_medicine_image.png"}
          alt={trimString(activePrinciple)}
        />
        <DataContainer isTreatmentCard={isTreatmentCard}>
          <MedicineName>{trimString(activePrinciple)}</MedicineName>
          {completeFormat && (
            <MedicineDescription>{trimString(completeFormat, 50)}</MedicineDescription>
          )}

          <SpacedRow>
            <MedicineDescription>{trimString(concentration)}</MedicineDescription>
            {showSincePrice && (
              <PriceRow>
                <MedicinePriceContainer>
                  <MedicinePriceText>Desde</MedicinePriceText>
                  <MedicinePriceAmount>${numberFormat(price)}</MedicinePriceAmount>
                </MedicinePriceContainer>
              </PriceRow>
            )}
          </SpacedRow>
        </DataContainer>
        {!isTreatmentCard && <ArrowForwardIcon />}
      </SpacedRow>
      {isTreatmentCard && (
        <ComparativeTable
          medicine={genericMedicine}
          modalOpen={modalOpen}
          changeModalOpenState={changeModalOpenState}
        />
      )}
      <LastUpdateModal
        medicine={genericMedicine}
        modalOpen={modalOpen}
        changeModalOpenState={changeModalOpenState}
      />
    </MedicineContainer>
  );
}

interface LastUpdateModalProps {
  medicine: SearchProduct | SearchGeneric;
  modalOpen: boolean;
  changeModalOpenState: () => void;
}

function LastUpdateModal({
  medicine,
  modalOpen,
  changeModalOpenState,
}: LastUpdateModalProps) {
  const lastUpdateMessage = useMemo(() => {
    const date = getOldestExternalPriceUpdateDate(medicine);

    if (!date) return "";
    const dateObj = new Date(date);
    const dateStr = format(dateObj, "dd.MM.yyyy");
    const timeStr = format(dateObj, "HH:mm");

    return `Actualizado el ${dateStr} a las ${timeStr} hrs. Los precios pueden haber variado desde entonces.`;
  }, [medicine]);

  return (
    <Modal title="Precios online" isOpen={modalOpen} onClose={changeModalOpenState}>
      <ModalContentContainer>
        <LastUpdateMessage>
          {lastUpdateMessage}
          <ModalContent>
            Buscamos los productos en farmaciameki.cl, cruzverde.cl, farmaciasahumada.cl,
            salcobrand.cl y drsimi.cl y te mostramos los precios de lista y descuentos
            generales de lo que logramos encontrar.
          </ModalContent>
          <ModalContent>
            Los descuentos pueden cambiar según el día de la semana, el convenio del
            cliente y/o el medio de pago.
          </ModalContent>
          <ModalContent>
            Si un producto no lo encontramos no significa que no exista en esas farmacias.
          </ModalContent>
          <ModalContent>
            Si tienes una farmacia y quieres aparecer,{" "}
            <ContactUsText
              onClick={() =>
                onContactUsClick(
                  "Hola, me gustaría que mi farmacia aparezca en MekiDoc. ¿Podrías darme más detalles de como realizar la incorporación?",
                )
              }
            >
              escríbenos aquí.
            </ContactUsText>
          </ModalContent>
        </LastUpdateMessage>
      </ModalContentContainer>
    </Modal>
  );
}

function ComparativeTable({
  medicine,
  modalOpen,
  changeModalOpenState,
}: {
  medicine: SearchProduct | SearchGeneric;
  modalOpen: boolean;
  changeModalOpenState: () => void;
}) {
  const formatPrice = (price: number) => `$${numberFormat(price)}`;

  const lastUpdateMessage = useMemo(() => {
    const date = getOldestExternalPriceUpdateDate(medicine);

    if (!date) return "";
    const dateObj = new Date(date);
    const dateStr = format(dateObj, "dd.MM.yyyy");
    const timeStr = format(dateObj, "HH:mm");

    return `Actualizado el ${dateStr} a las ${timeStr} hrs.`;
  }, [medicine]);

  const getMedicineDetailsFromSeller = useCallback(
    (sellerKey: string) => {
      const sellerInfo = medicine.externalPrices?.find(
        (sellerInfo: ExternalPrice) => sellerInfo.seller === sellerKey,
      ) as ExternalPrice | undefined;

      const medicineDetails = {
        medicineName: "",
        amount: sellerInfo ? sellerInfo.amount : "No encontrado",
        saleAmount: sellerInfo ? sellerInfo.saleAmount : undefined,
      };

      if (sellerInfo?.product) {
        const { name, activePrinciple, laboratory, quantity, unit } = sellerInfo.product;

        medicineDetails.medicineName = trimString(
          `${name && name !== activePrinciple ? name : laboratory} ${
            (quantity ?? 0) !== 0 && unit !== "" ? ` · ${quantity} ${unit}` : ""
          }`,
          256,
        );
      }

      return medicineDetails;
    },
    [medicine],
  );

  const { sortedDataWithPrice, sortedDataWithoutPrice, minPrice } = useMemo(() => {
    const sellersPrices = Object.entries(pharmacies).map(
      ([sellerKey, sellerFormattedName]) => {
        const { medicineName, amount, saleAmount } =
          getMedicineDetailsFromSeller(sellerKey);

        return {
          seller: sellerFormattedName,
          medicineName,
          amount: typeof amount === "number" ? amount : Infinity,
          saleAmount: saleAmount ?? Infinity,
        };
      },
    );

    const minPrice = sellersPrices.reduce((min, seller) => {
      const minSellerPrice = Math.min(seller.amount, seller.saleAmount);

      return Math.min(min, minSellerPrice);
    }, Infinity);

    const sortedCompareTableData = sellersPrices.sort((a, b) => {
      const minPriceA = Math.min(a.amount, a.saleAmount);
      const minPriceB = Math.min(b.amount, b.saleAmount);

      return minPriceA - minPriceB;
    });

    const { sortedDataWithPrice, sortedDataWithoutPrice } = sortedCompareTableData.reduce(
      (
        acc: {
          sortedDataWithPrice: typeof sortedCompareTableData;
          sortedDataWithoutPrice: typeof sortedCompareTableData;
        },
        row,
      ) => {
        if (row.amount === Infinity) {
          acc.sortedDataWithoutPrice.push(row);
        } else {
          acc.sortedDataWithPrice.push(row);
        }

        return acc;
      },
      { sortedDataWithPrice: [], sortedDataWithoutPrice: [] },
    );

    return { sortedDataWithPrice, sortedDataWithoutPrice, minPrice };
  }, [getMedicineDetailsFromSeller]);

  return (
    <ComparativeTableContainer>
      <HeaderRow>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          <Typography fontSize="1rem" fontWeight={600}>
            Precios Online
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lastUpdateMessage}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <StyledIconButton size="small" onClick={changeModalOpenState}>
            <HelpRoundedIcon />
          </StyledIconButton>
        </Box>
      </HeaderRow>

      {sortedDataWithPrice.map((item, index) => {
        const isMinPrice = Math.min(item.amount, item.saleAmount) === minPrice;

        const medicineName =
          item.seller === pharmacies.farmaciaMeki
            ? item.medicineName
            : item.medicineName?.replace(/cenabast/i, "");

        return (
          <ComparativeTableRow key={item.seller}>
            {isMinPrice ? <StyledPremiumIcon /> : <RowNumber>{index + 1}.</RowNumber>}
            <ComparativeTableDataRow>
              <MedicineInfoColumn>
                <PharmacyInfo isMinPrice={isMinPrice}>{item.seller}</PharmacyInfo>
                {medicineName && <NameInfo>{medicineName}</NameInfo>}
              </MedicineInfoColumn>
              <MedicinePriceColumn>
                {item.saleAmount !== Infinity && item.saleAmount !== item.amount ? (
                  <>
                    <ExpensivePrice>{formatPrice(item.amount)}</ExpensivePrice>
                    <Price isMinPrice={isMinPrice}>{formatPrice(item.saleAmount)}</Price>
                  </>
                ) : (
                  <Price isMinPrice={isMinPrice}>{formatPrice(item.amount)}</Price>
                )}
              </MedicinePriceColumn>
            </ComparativeTableDataRow>
          </ComparativeTableRow>
        );
      })}
      {sortedDataWithoutPrice.length > 0 && (
        <PriceNotFoundContainer>
          <PriceNotFound>No pudimos encontrar el producto en:</PriceNotFound>
          <PriceNotFound isSeller={true}>
            {sortedDataWithoutPrice.map(({ seller }) => seller).join(", ")}
          </PriceNotFound>
        </PriceNotFoundContainer>
      )}
      <LastUpdateModal
        medicine={medicine}
        modalOpen={modalOpen}
        changeModalOpenState={changeModalOpenState}
      />
    </ComparativeTableContainer>
  );
}
